import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  RepositorioActuadores,
  repositorioActuadores,
} from '../../../data/repositories/RepositorioActuadores';
import { Actuador } from '../../../domain/entities/Actuador';
import {
  EstadoComando,
  type EstadoComandoValor,
} from '../../../domain/value-objects/EstadoComando';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useCascadaFiltros } from '../../hooks/useCascadaFiltros';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { useSesionStore } from '../../stores/sesionStore';

/**
 * Margen de espera de la confirmación del equipo.
 *
 * Pasado este tiempo la orden se marca como sin respuesta. No significa que no
 * se aplicara: significa que no lo sabemos, y es más honesto decirlo que dejar
 * el botón girando indefinidamente.
 */
const ESPERA_CONFIRMACION_MS = 5000;

/** Cuánto permanece visible el resultado antes de volver al estado neutro. */
const VISIBILIDAD_RESULTADO_MS = 6000;

export interface LineaActividad {
  clave: string;
  actuador: string;
  texto: string;
  estado: EstadoComandoValor;
  hora: Date;
}

/**
 * Estado de la pantalla de control.
 *
 * Lleva la cuenta de en qué punto está cada orden enviada. Esa contabilidad es
 * la razón de ser de la pantalla: sin ella el operador pulsa un botón y no
 * sabe si el equipo llegó a enterarse.
 */
export function useCentroControl() {
  const clienteConsultas = useQueryClient();
  const cascada = useCascadaFiltros({}, 'control');
  const politica = useSesionStore((s) => s.politica);

  const [busqueda, setBusqueda] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<'' | 'on' | 'off'>('');
  const [estados, setEstados] = useState<Record<number, EstadoComandoValor>>({});
  const [actividad, setActividad] = useState<LineaActividad[]>([]);

  /** Temporizadores de espera, uno por actuador con orden en curso. */
  const temporizadores = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  // Un temporizador que sobreviva al desmontaje intentaría actualizar estado
  // de un componente que ya no existe.
  useEffect(() => {
    const pendientes = temporizadores.current;
    return () => {
      for (const t of pendientes.values()) clearTimeout(t);
      pendientes.clear();
    };
  }, []);

  const filtros = useMemo(
    () => ({
      clienteId: cascada.seleccion.clienteId,
      areaId: cascada.seleccion.areaId,
      nodoId: cascada.seleccion.nodoId,
    }),
    [cascada.seleccion],
  );

  const consulta = useQuery({
    queryKey: ['actuadores', filtros],
    queryFn: () => repositorioActuadores.listar(filtros),
  });

  const actuadores = useMemo(() => consulta.data ?? [], [consulta.data]);

  const filtrados = useMemo(
    () =>
      actuadores.filter((a) => {
        if (!a.coincideCon(busqueda)) return false;
        if (tipoFiltro && a.tipo !== tipoFiltro) return false;
        if (estadoFiltro === 'on' && a.encendido !== true) return false;
        if (estadoFiltro === 'off' && a.encendido !== false) return false;
        return true;
      }),
    [actuadores, busqueda, tipoFiltro, estadoFiltro],
  );

  /** Tipos presentes en el listado, para el desplegable. */
  const tiposDisponibles = useMemo(
    () => [...new Set(actuadores.map((a) => a.tipo).filter(Boolean))].sort(),
    [actuadores],
  );

  const refrescar = useCallback(() => {
    void clienteConsultas.invalidateQueries({ queryKey: ['actuadores'] });
  }, [clienteConsultas]);

  const anotar = useCallback((actuador: Actuador, texto: string, estado: EstadoComandoValor) => {
    setActividad((previas) =>
      [
        {
          clave: `${actuador.id}-${estado}-${previas.length}`,
          actuador: actuador.nombre,
          texto,
          estado,
          hora: new Date(),
        },
        ...previas,
      ].slice(0, 40),
    );
  }, []);

  const detenerEspera = useCallback((actuadorId: number) => {
    const t = temporizadores.current.get(actuadorId);
    if (t) {
      clearTimeout(t);
      temporizadores.current.delete(actuadorId);
    }
  }, []);

  const fijarEstado = useCallback((actuadorId: number, estado: EstadoComandoValor) => {
    setEstados((previos) => ({ ...previos, [actuadorId]: estado }));
  }, []);

  /** Devuelve el actuador al estado neutro pasado un rato. */
  const olvidarResultado = useCallback((actuadorId: number) => {
    setTimeout(() => {
      setEstados((previos) => {
        const copia = { ...previos };
        delete copia[actuadorId];
        return copia;
      });
    }, VISIBILIDAD_RESULTADO_MS);
  }, []);

  /**
   * Confirmaciones de los equipos.
   *
   * El aviso no se filtra por actuador: la clave de la tabla es el
   * identificador de la confirmación, no el del actuador, y filtrar por una
   * columna que no es la clave deja la suscripción muda sin avisar.
   */
  useTiempoReal({ tabla: 'actuador_acks', evento: 'INSERT' }, (payload) => {
    const confirmacion = RepositorioActuadores.leerConfirmacion(
      (payload.new ?? {}) as Record<string, unknown>,
    );
    if (!confirmacion) return;

    const { actuadorId, aplicado, estado } = confirmacion;

    // Sólo interesa si teníamos una orden esperando respuesta.
    const actual = estados[actuadorId];
    if (actual !== 'encolado' && actual !== 'pendiente' && actual !== 'sin_respuesta') return;

    detenerEspera(actuadorId);

    const nuevo: EstadoComandoValor = aplicado ? 'aplicado' : 'rechazado';
    fijarEstado(actuadorId, nuevo);
    olvidarResultado(actuadorId);

    const actuador = actuadores.find((a) => a.id === actuadorId);
    if (actuador) {
      anotar(
        actuador,
        aplicado ? 'Orden aplicada' : `Orden rechazada${estado ? ` (${estado})` : ''}`,
        nuevo,
      );
    }

    refrescar();
  });

  /**
   * Envía una orden y sigue su recorrido.
   * `duracionPulso` alarga la espera: un pulso de tres segundos no puede
   * confirmarse antes de que termine.
   */
  const enviar = useCallback(
    async (
      actuador: Actuador,
      accion: () => Promise<void>,
      descripcion: string,
      duracionPulso = 0,
    ) => {
      if (!politica?.puedeOperarActuadores) {
        toast.error('Tu usuario no tiene habilitado el control de actuadores');
        return;
      }

      fijarEstado(actuador.id, 'encolado');
      anotar(actuador, descripcion, 'encolado');

      try {
        await accion();
      } catch (e) {
        registrarError('control.enviar', e);
        detenerEspera(actuador.id);
        fijarEstado(actuador.id, 'error');
        olvidarResultado(actuador.id);
        anotar(actuador, 'No se pudo enviar la orden', 'error');
        toast.error(e instanceof ErrorApp ? e.message : 'No se pudo enviar la orden');
        return;
      }

      // El servidor aceptó la orden; ahora toca esperar al equipo.
      fijarEstado(actuador.id, 'pendiente');

      const temporizador = setTimeout(() => {
        temporizadores.current.delete(actuador.id);
        fijarEstado(actuador.id, 'sin_respuesta');
        olvidarResultado(actuador.id);
        anotar(actuador, 'El equipo no confirmó la orden', 'sin_respuesta');
      }, ESPERA_CONFIRMACION_MS + duracionPulso);

      temporizadores.current.set(actuador.id, temporizador);
    },
    [politica, fijarEstado, anotar, detenerEspera, olvidarResultado],
  );

  const encender = useCallback(
    (a: Actuador) => enviar(a, () => repositorioActuadores.encender(a.id), 'Encender'),
    [enviar],
  );

  const apagar = useCallback(
    (a: Actuador) => enviar(a, () => repositorioActuadores.apagar(a.id), 'Apagar'),
    [enviar],
  );

  const pulsar = useCallback(
    (a: Actuador, ms: number) => {
      const problema = Actuador.validarPulso(ms);
      if (problema) {
        toast.error(problema);
        return Promise.resolve();
      }
      return enviar(a, () => repositorioActuadores.pulsar(a.id, ms), `Pulso de ${ms} ms`, ms);
    },
    [enviar],
  );

  const estadoDe = useCallback(
    (actuadorId: number) => EstadoComando.from(estados[actuadorId] ?? 'inactivo'),
    [estados],
  );

  return {
    cascada,
    actuadores: filtrados,
    total: actuadores.length,
    cargando: consulta.isLoading,
    error: consulta.isError,
    recargando: consulta.isFetching,

    busqueda,
    tipoFiltro,
    setTipoFiltro,
    estadoFiltro,
    setEstadoFiltro,
    tiposDisponibles,
    setBusqueda,
    actividad,
    limpiarActividad: () => setActividad([]),

    estadoDe,
    puedeOperar: politica?.puedeOperarActuadores ?? false,
    motivoSinPermiso: politica?.motivoDenegacion('operar_actuadores') ?? null,

    encender,
    apagar,
    pulsar,
    refrescar,
  };
}
